import {
  type JobCheckoutViewModelFactory,
  type JobCreationRootViewModelFactory,
  type JobsFeatureOptions,
  type PromptCreateJobFn,
  type UserJobsTabViewModelFactory,
  type QuestionListViewModelFactory,
} from '@/features/jobs'
import { UserJobsTabViewModel } from '@/features/jobs/list/UserJobsTabViewModel'
import { FirmDashboardViewModel } from '@/features/jobs/dashboard/FirmDashboardViewModel'
import {
  JobManagementViewModel,
  type JobManagementViewModelOptions,
} from '@/features/jobs/list/JobManagementViewModel'
import { JobManagementSelectionViewModel } from '@/features/jobs/list/JobManagementSelectionViewModel'
import { createJobSearchViewModelFactory } from '@/features/jobs/list/JobSearchViewModel'
import { JobsTableColumnsViewModel } from '@/features/jobs/list/JobsTableColumnsViewModel'
import { JobListPageViewModel } from '@/features/jobs/list/JobListPageViewModel'
import { UpcomingConsultationPreviewViewModel } from '@/features/jobs/list/UpcomingConsultationPreviewViewModel'
import { NotifyProsOfUnclaimedJobsDialogViewModel } from '@/features/jobs/list/components/JobManagement/NotifyProsOfUnclaimedJobsDialog/NotifyProsOfUnclaimedJobsDialogViewModel'
import { type Job } from '@/features/jobs/Job'
import { observable, runInAction } from 'mobx'
import { memoize } from '@/utils/memoize-util'
import { SearchableJobStore } from '@/features/jobs/list/SearchableJobStore'
import { CheckoutViewModel } from '@/features/jobs/drafting/checkout/CheckoutViewModel'
import {
  ApplyCouponAsAdminDialogViewModel,
  type ApplyCouponAsAdminDialogViewModelFactory,
} from '@/features/jobs/ApplyCouponAsAdminDialog/ApplyCouponAsAdminDialogViewModel'
import { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { JobConversationViewModel } from '@/features/jobs/details/JobConversationViewModel'
import { JobCollaboratorsViewModel } from '@/features/jobs/details/JobCollaboratorsViewModel'
import { JobBillingViewModel } from '@/features/jobs/details/JobBillingViewModel'
import { JobScopeChangesViewModel } from '@/features/jobs/details/JobScopeChangesViewModel'
import { JobCapturedInformationViewModel } from '@/features/jobs/details/JobCapturedInformationViewModel'
import { JobConsultationViewModel } from '@/features/jobs/details/JobConsultationViewModel'
import { JobBookkeepingViewModel } from '@/features/jobs/details/JobBookkeepingViewModel'
import { JobActionsViewModel } from '@/features/jobs/details/JobActionsViewModel'
import { JobProgressionViewModel } from '@/features/jobs/details/components/JobProgressionCard/JobProgressionViewModel'
import { RatingsViewModel } from '@/features/jobs/details/ratings/RatingsViewModel'
import { AddCollaboratorsAsAdminDialogViewModel } from '@/features/jobs/details/components/AddCollaboratorsAsAdminDialog/AddCollaboratorsAsAdminDialogViewModel'
import { RemoveCollaboratorsAsAdminDialogViewModel } from '@/features/jobs/details/components/RemoveCollaboratorsAsAdminDialog/RemoveCollaboratorsAsAdminDialogViewModel'
import { AddClientsAsTeamMemberDialogViewModel } from '@/features/jobs/details/components/AddClientsAsTeamMemberDialog/AddClientsAsTeamMemberDialogViewModel'
import { RemoveClientsAsNonAdminDialogViewModel } from '@/features/jobs/details/components/RemoveClientsAsNonAdminDialog/RemoveClientsAsNonAdminDialogViewModel'
import { RemoveClientTeamDialogViewModel } from '@/features/jobs/details/components/RemoveClientTeamDialog/RemoveClientTeamDialogViewModel'
import { TransferJobDialogViewModel } from '@/features/jobs/TransferJobDialog/TransferJobDialogViewModel'
import { RemoveCouponAsAdminDialogViewModel } from '@/features/jobs/RemoveCouponAsAdminDialog/RemoveCouponAsAdminDialogViewModel'
import { ChargeOrRefundDialogViewModel } from '@/features/jobs/details/components/ChargeOrRefundDialog/ChargeOrRefundDialogViewModel'
import { AddManualInvoiceDialogViewModel } from '@/features/jobs/details/components/AddManualInvoiceDialog/AddManualInvoiceDialogViewModel'
import { SplitPayoutDialogViewModel } from '@/features/jobs/details/components/SplitPayoutDialog/SplitPayoutDialogViewModel'
import { RequestScopeChangesDialogViewModel } from '@/features/jobs/details/components/RequestScopeChangesDialog/RequestScopeChangesDialogViewModel'
import { ViewJobScopeChangeRequestDialogViewModel } from '@/features/jobs/details/components/ViewJobScopeChangeRequestDialog/ViewJobScopeChangeRequestDialogViewModel'
import { ScopeChangeBannerViewModel } from '@/features/jobs/details/ScopeChangeBannerViewModel'
import { UpdateJobScopeDialogViewModel } from '@/features/jobs/details/components/UpdateJobScopeDialog/UpdateJobScopeDialogViewModel'
import { ServiceItemListDialogViewModel } from '@/features/jobs/details/components/ServiceItemListDialog/ServiceItemListDialogViewModel'
import { DetailsViewModel } from '@/features/jobs/details/DetailsViewModel'
import { InfoGatheringCardViewModel } from '@/features/jobs/details/components/InfoGatheringCard/InfoGatheringCardViewModel'
import { NotesTabViewModel } from '@/features/jobs/details/components/CapturedInformationCard/NotesTabViewModel'
import { NotesDialogViewModel } from '@/features/jobs/details/components/CapturedInformationCard/NotesDialog/NotesDialogViewModel'
import { EditPersonalProfileDialogViewModel } from '@/features/jobs/info-gathering/individual-info/components/EditPersonalProfileDialog/EditPersonalProfileDialogViewModel'
import { EditBusinessProfileDialogViewModel } from '@/features/jobs/info-gathering/business-info/components/EditBusinessProfileDialog/EditBusinessProfileDialogViewModel'
import { EditRefundDialogViewModel } from '@/features/jobs/info-gathering/tax-refund-method/components/EditRefundDialog/EditRefundDialogViewModel'
import { DocumentRequestDialogViewModel } from '@/features/jobs/details/components/DocumentRequestDialog/DocumentRequestDialogViewModel'
import { ConsultationPendingBookingModalViewModel } from '@/features/jobs/details/components/ConsultationPendingBookingModal/ConsultationPendingBookingModalViewModel'
import { IncompleteConsultationCompletionDialogViewModel } from '@/features/jobs/details/components/IncompleteConsultationCompletionDialog/IncompleteConsultationCompletionDialogViewModel'
import { BookkeepingGrantAccessDialogViewModel } from '@/features/jobs/details/components/BookkeepingGrantAccessDialog/BookkeepingGrantAccessDialogViewModel'
import { BookkeepingCorrectCycleDialogViewModel } from '@/features/jobs/details/components/BookkeepingCorrectCycleDialog/BookkeepingCorrectCycleDialogViewModel'
import { BookkeepingSelectCycleDialogViewModel } from '@/features/jobs/details/components/BookkeepingSelectCycleDialog/BookkeepingSelectCycleDialogViewModel'
import { BookkeepingProgressionViewModel } from '@/features/jobs/details/components/BookkeepingProgressionCard/BookkeepingProgressionViewModel'
import { RenameJobDialogViewModel } from '@/features/jobs/RenameJobDialog/RenameJobDialogViewModel'
import { ForfeitDialogViewModel } from '@/features/jobs/details/components/ForfeitDialog/ForfeitDialogViewModel'
import { CorrectionsDialogViewModel } from '@/features/jobs/details/components/CorrectionsDialog/CorrectionsDialogViewModel'
import { CreateAmendmentDialogViewModel } from '@/features/jobs/details/components/AmendmentsCard/CreateAmendmentDialog/CreateAmendmentDialogViewModel'
import { CopyJobDialogViewModel } from '@/features/jobs/CopyJobDialog/CopyJobDialogViewModel'
import { CloseJobAsAdminDialogViewModel } from '@/features/jobs/details/components/CloseJobAsAdminDialog/CloseJobAsAdminDialogViewModel'
import { DeleteJobDialogViewModel } from '@/features/jobs/details/components/DeleteJobDialog/DeleteJobDialogViewModel'
import { NotifyProsOfOpenJobDialogViewModel } from '@/features/jobs/details/components/NotifyProsOfOpenJobDialog/NotifyProsOfOpenJobDialogViewModel'
import { SubmitJobDialogViewModel } from '@/features/jobs/details/components/SubmitJobDialog/SubmitJobDialogViewModel'
import { UpdateDeadlineDialogViewModel } from '@/features/jobs/details/components/UpdateDeadlineDialog/UpdateDeadlineDialogViewModel'
import { ReopenJobAsAdminDialogViewModel } from '@/features/jobs/details/components/ReopenJobAsAdminDialog/ReopenJobAsAdminDialogViewModel'
import { FinalReturnAmountsCardViewModel } from '@/features/jobs/details/components/FinalReturnAmountsCard/FinalReturnAmountsCardViewModel'
import { JobNotesViewModel } from '@/features/jobs/details/components/JobNotesCard/JobNotesViewModel'
import { JobTicketsViewModel } from '@/features/jobs/details/JobTicketsViewModel'
import { JobInsightsCardViewModel } from '@/features/jobs/details/components/JobInsightsCard/JobInsightsCardViewModel'
import { TxfJobCardViewModel } from '@/features/jobs/details/components/TxfJobCard/TxfJobCardViewModel'
import { JobListViewModel } from '@/features/jobs/details/components/JobList/JobListViewModel'
import { Member } from '@/features/iam/members/Member'
import { Team } from '@/features/iam/teams/Team'
import { Conversation } from '@/features/messaging/Conversation'
import { ValueRetrievablePromise } from '@/utils/value-retrievable-promise'
import { isJobListAutoHidden$ } from '@/hooks/viewport-hooks'
import { isRefreshDesign } from '@/infra/design'
import {
  type FetchCheckoutLegendVersionFn,
  JobCheckoutViewModel,
} from '@/features/jobs/drafting/checkout/JobCheckoutViewModel'
import { DocumentManualVerificationModalViewModel } from '@/features/jobs/details/components/DocumentManualVerificationModal/DocumentManualVerificationModalViewModel'
import { JobRootViewModel } from '@/features/jobs/details/JobRootViewModel'
import { OCRScopingJobsViewModel } from '@/features/jobs/scoping/OCRScopingJobsViewModel'
import { JobsApi } from '@/features/jobs/api/job-api'
import { JobProgressionStore } from '@/features/jobs/taxes/progression/JobProgressionStore'
import { LegendsApi } from '@/features/jobs/api/legend-api'
import { WunderApi } from '@/features/jobs/api/wunder-api'
import { BookkeepingProgressionStore } from '@/features/jobs/bookkeeping/BookkeepingProgressionStore'
import { ConsultationStore } from '@/features/jobs/consultations/ConsultationStore'
import { CreateJobDialogViewModel } from '@/features/jobs/drafting/components/CreateJobDialog/CreateJobDialogViewModel'
import { PrefilledDraftInput } from '@/features/jobs/drafting/PrefilledDraftInput'
import { JobCreationRootViewModel } from '@/features/jobs/drafting/JobCreationRootViewModel'
import { DocumentRequestStore } from '@/features/jobs/details/document-requests/DocumentRequestStore'
import { BusinessProfileStore } from '@/features/jobs/info-gathering/business-info/BusinessProfileStore'
import { IndividualProfileStore } from '@/features/jobs/info-gathering/individual-info/IndividualProfileStore'
import { TaxRefundMethodStore } from '@/features/jobs/info-gathering/tax-refund-method/TaxRefundMethodStore'
import { InsightStore } from '@/features/jobs/details/insights/InsightStore'
import { JobStatsStore } from '@/features/jobs/dashboard/job-stats/JobStatsStore'
import {
  type PrimePublishedLegendFn,
  PublishedLegendStore,
} from '@/features/jobs/legends/PublishedLegendStore'
import { JobNoteStore } from '@/features/jobs/details/notes/JobNoteStore'
import { AvailableJobProgressionFlowsStore } from '@/features/jobs/taxes/progression/available-job-progression-flows/AvailableJobProgressionFlowsStore'
import { RatingsStore } from '@/features/jobs/details/ratings/RatingsStore'
import { SureprepStore } from '@/features/jobs/details/sureprep/SureprepStore'
import { JobTicketStore } from '@/features/jobs/tickets/JobTicketStore'
import { OCRScopeWorkStore } from '@/features/jobs/scoping/OCRScopeWorkStore'
import { incomingJobMessageNotifications } from '@/features/jobs/details/incoming-job-message-notifications'
import { toStream } from '@/utils/mobx-util'
import { links } from '@/utils/link'
import { FilterStorage } from '@/infra/FiltersViewModel'
import { env } from '@/infra/env'
import { Id } from '@/infra/id'
import { localStorage } from '@/infra/storage'
import { SavedJobFilterStore } from '@/features/jobs/list/SavedJobFilterStore'
import { InfoGatheringStore } from '@/features/jobs/info-gathering/InfoGatheringStore'
import { createWorkspaceSelectorViewModelFactory } from '@/features/jobs/drafting/WorkspaceSelectorViewModel'
import { createGetDraftingMembersFn } from '@/features/jobs/drafting/drafting-members'
import { createOwnerSelectorViewModelFactory } from '@/features/jobs/drafting/OwnerSelectorViewModel'
import { createTeamSelectorViewModelFactory } from '@/features/jobs/drafting/TeamSelectorViewModel'
import { createSupportingClientSelectorViewModelFactory } from '@/features/jobs/drafting/SupportingClientSelectorViewModel'
import { createRequestProviderSelectorViewModelFactory } from '@/features/jobs/drafting/RequestProviderSelectorViewModel'
import { createLegendSelectorViewModelFactory } from '@/features/jobs/drafting/LegendSelectorViewModel'
import { createIntakeQuestionsViewModelFactory } from '@/features/jobs/drafting/IntakeQuestionsViewModel'
import { createPreviousProviderSelectionViewModelFactory } from '@/features/jobs/drafting/PreviousProviderSelectionViewModel'
import { createSubmissionViewModelFactory } from '@/features/jobs/drafting/SubmissionViewModel'
import { createBillingViewModelFactory } from '@/features/jobs/drafting/BillingViewModel'
import { createCouponSelectionViewModelFactory } from '@/features/jobs/drafting/CouponSelectionViewModel'
import { CouponModalViewModel } from '@/features/jobs/drafting/components/JobCreation/Submission/CouponModal/CouponModalViewModel'
import { createDraftPersisterFactory } from '@/features/jobs/drafting/DraftPersister'
import { createDraftJobViewModelFactory } from '@/features/jobs/drafting/DraftJobViewModel'
import { QuestionListViewModel } from '@/features/jobs/questions/QuestionListViewModel'
import { DefaultQuestionAnswerEditorViewModelFactory } from '@/features/jobs/questions/QuestionAnswerEditorViewModelFactory'
import { JobDocumentsViewModel } from '@/features/jobs/details/JobDocumentsViewModel'
import { JobStore } from './JobStore'
import { ProviderStore } from './ProviderStore'
import { QuestionStore } from '@/features/jobs/questions/QuestionStore'
import { DraftJobStore } from '@/features/jobs/drafting/DraftJobStore'
import { JobScopeChangeStore } from '@/features/jobs/job-scope-changes/JobScopeChangeStore'
import { TxfJobStore } from '@/features/jobs/details/txf/TxfJobStore'

/**
 * Acquires the view model for a job's details, keeping it for the session so
 * returning to a job shows it as it was left.
 */
export type AcquireJobDetailsViewModelFn = (
  job: Job,
  signal?: AbortSignal,
) => Promise<JobDetailsViewModel>

export function InternalJobsFeature(options: JobsFeatureOptions) {
  const { token$ } = options

  const api = new JobsApi({ baseURL: env.WORK_DOTNET_API, token$ })
  const workRealtime$ = api.realtime$
  const legendsApi = new LegendsApi({ baseURL: env.LEGEND_API, token$ })
  const wunderApi = new WunderApi({ baseURL: env.WUNDER_API, token$ })
  const progressionStore = new JobProgressionStore(api)

  const sureprepStore = new SureprepStore(api)

  const publishedLegendStore = new PublishedLegendStore(
    legendsApi,
    options.primeWorkspace,
  )
  const primePublishedLegend: PrimePublishedLegendFn = (versionId) =>
    publishedLegendStore.primePublishedLegend(versionId)

  const jobStore = new JobStore(
    api,
    options.getCurrentMember,
    options.primeConversation,
    options.primeWorkspace,
    options.primeUser,
    options.primeMember,
    options.primeTeam,
    primePublishedLegend,
    options.newMessages$,
    options.fetchBillingTransactions,
    workRealtime$,
    options.primeMemberByPublicId,
    options.primeRoom,
  )

  const jobStatsStore = new JobStatsStore(api)

  const searchableJobStore = new SearchableJobStore(
    api,
    options.primeWorkspace,
    options.primeConversation,
    primePublishedLegend,
    options.primeTeam,
    () => options.getCurrentUser().publicId ?? null,
    workRealtime$,
    jobStore.localJobChanges$,
  )

  const draftJobStore = new DraftJobStore(
    api,
    jobStore,
    options.fetchBillingTransactions,
  )
  const jobScopeChangeStore = new JobScopeChangeStore(api, jobStore)
  const questionStore = new QuestionStore(api)
  const providerStore = new ProviderStore(
    api,
    options.primeMemberByPublicId,
    options.fetchMembersByPublicIds,
    options.listQualifiedProviders,
  )

  const jobTicketStore = new JobTicketStore(api)
  const jobNoteStore = new JobNoteStore(
    api,
    workRealtime$,
    options.primeMemberByPublicId,
  )
  const jobInsightStore = new InsightStore(api)
  const ratingsStore = new RatingsStore(api)
  const individualProfileStore = new IndividualProfileStore(api)
  const businessProfileStore = new BusinessProfileStore(api)
  const taxRefundMethodStore = new TaxRefundMethodStore(api)

  const infoGatheringStore = new InfoGatheringStore(
    api,
    options.primeDocument,
    options.primeDocumentTypeByShortId,
  )

  const consultationStore = new ConsultationStore(
    api,
    jobStore.fetchJob,
    options.primeRoom,
  )

  const ocrScopeWorkStore = new OCRScopeWorkStore(
    wunderApi,
    publishedLegendStore,
  )
  const txfJobStore = new TxfJobStore(api)
  const availableJobProgressionFlowsStore =
    new AvailableJobProgressionFlowsStore(api)
  const bookkeepingProgressionStore = new BookkeepingProgressionStore(api)
  const documentRequestStore = new DocumentRequestStore(api)

  const createApplyCouponAsAdminViewModel: ApplyCouponAsAdminDialogViewModelFactory =
    (job) =>
      new ApplyCouponAsAdminDialogViewModel(
        job,
        jobStore,
        options.fetchMemberCoupons,
        options.listCoupons,
        options.newAlert,
        options.createAlertErrorHandler,
        options.createAlertRetryWrapper,
      )

  const getDraftingMembers = createGetDraftingMembersFn(
    options.getCurrentUserWorkspaceMemberships,
    options.getTeamMembersForWorkspaceMember,
    options.getCurrentMember,
  )

  const couponModalVm = new CouponModalViewModel()

  const createDraftJobViewModel = createDraftJobViewModelFactory(
    createWorkspaceSelectorViewModelFactory(
      getDraftingMembers,
      options.listWorkspaceMemberships,
      options.getCurrentMember,
      publishedLegendStore,
    ),
    createOwnerSelectorViewModelFactory(
      options.fetchMember,
      options.searchMembers,
      options.createAlertRetryWrapper,
    ),
    createTeamSelectorViewModelFactory(
      options.getCurrentMember,
      options.getTeamMember,
      options.listUserTeamMembersForWorkspace,
      options.getTeamMembersForWorkspaceMember,
      options.createAlertRetryWrapper,
    ),
    createSupportingClientSelectorViewModelFactory(
      options.searchMembers,
      options.createAlertRetryWrapper,
    ),
    createRequestProviderSelectorViewModelFactory(
      options.getWorkspaceMembers,
      options.searchMembers,
      options.createAlertRetryWrapper,
    ),
    createLegendSelectorViewModelFactory(
      publishedLegendStore,
      options.newAlert,
      options.createAlertRetryWrapper,
    ),
    createIntakeQuestionsViewModelFactory(
      (job, createDocumentRequestsViewModel) =>
        new QuestionListViewModel(
          new DefaultQuestionAnswerEditorViewModelFactory({
            createDocumentRequestsViewModel: () =>
              createDocumentRequestsViewModel(job),
            getAsOfDate: () => job.dateCreated,
          }),
        ),
      options.createDocumentRequestsViewModel,
      questionStore,
      options.createAlertRetryWrapper,
    ),
    createPreviousProviderSelectionViewModelFactory(
      providerStore,
      options.createAlertRetryWrapper,
    ),
    createSubmissionViewModelFactory(
      draftJobStore,
      options.navigate,
      options.createAlertErrorHandler,
      options.createAlertRetryWrapper,
      createBillingViewModelFactory(
        options.fetchBillingMethods,
        options.canManageBilling,
        options.promptSelectPaymentMethod,
        options.promptNewPaymentMethod,
        createCouponSelectionViewModelFactory(
          options.addCoupon,
          options.fetchMemberCoupons,
          options.fetchCoupon,
          couponModalVm,
          options.newAlert,
        ),
        options.createAlertErrorHandler,
        options.createAlertRetryWrapper,
      ),
      options.getCurrentMember,
      options.getTeamMember,
    ),
    createDraftPersisterFactory(
      jobStore,
      draftJobStore,
      options.changeDefaultBillingMethod,
    ),
    jobStore,
    draftJobStore,
    options.newAlert,
    options.createAlertErrorHandler,
    options.navigate,
    options.renameShortcut$,
  )

  const createJobCreationRootViewModel: JobCreationRootViewModelFactory = () =>
    new JobCreationRootViewModel(
      createDraftJobViewModel,
      options.getCurrentMember,
      getDraftingMembers,
      options.getTeamMember,
      options.listUserTeamMembers,
      options.navigate,
    )

  const jobCreationRootVm = createJobCreationRootViewModel()

  const jobCreationDialogViewModel = new CreateJobDialogViewModel(
    options.navigate,
    options.newAlert,
    jobCreationRootVm,
  )

  const promptCreateJob: PromptCreateJobFn = async (prefill) => {
    await jobCreationDialogViewModel.open(
      undefined,
      prefill &&
        new PrefilledDraftInput(
          prefill.legendId,
          prefill.userPublicId,
          prefill.teamId,
          prefill.answers ?? [],
          prefill.ownerScopedInput,
        ),
    )
  }

  const documentManualVerificationVm =
    new DocumentManualVerificationModalViewModel(
      options.downloadDocument,
      options.confirmDocument,
      options.archiveDocument,
      options.newAlert,
      options.createAlertErrorHandler,
    )

  const jobDetailsVms = observable.set<JobDetailsViewModel>([], {
    deep: false,
  })

  const acquireJobDetailsViewModel: AcquireJobDetailsViewModelFn = memoize(
    async (job, signal) => {
      const {
        createConversationViewModel,
        createRoomViewModel,
        createMessageBankDialogViewModel,
        createDocumentPickerViewModel,
        createDocumentRequestsViewModel,
        createUploadBarViewModel,
        createDocumentCreationModalViewModel,
        createJobDocumentListViewModel,
      } = await ValueRetrievablePromise.props({
        createConversationViewModel: options.createConversationViewModel,
        createRoomViewModel: options.createRoomViewModel,
        createMessageBankDialogViewModel:
          options.createMessageBankDialogViewModel,
        createDocumentPickerViewModel: options.createDocumentPickerViewModel,
        createDocumentRequestsViewModel:
          options.createDocumentRequestsViewModel,
        createUploadBarViewModel: options.createUploadBarViewModel,
        createDocumentCreationModalViewModel:
          options.createDocumentCreationModalViewModel,
        createJobDocumentListViewModel: options.createJobDocumentListViewModel,
      })
      signal?.throwIfAborted()

      const root = jobRootViewModel
      const jobWorkspaceMember = () =>
        options.getCurrentMember(job.workspace.id) ?? Member.none
      const getChecklist = () => capturedInformationVm.checklist
      const isFirmAdminInWorkspace = (workspaceId?: Id) =>
        root.isFirmAdminInWorkspace(workspaceId)
      const createQuestionListViewModel = () =>
        new QuestionListViewModel(
          new DefaultQuestionAnswerEditorViewModelFactory({
            getAsOfDate: () => job.dateCreated,
            createDocumentRequestsViewModel: () =>
              createDocumentRequestsViewModel(job),
          }),
        )

      const documentsVm = new JobDocumentsViewModel(
        job,
        () => vm.isPartOfClientTeam,
        () => vm.isPartOfProTeam,
        options.newAlert,
        options.createAlertErrorHandler,
        options.showDocumentDetails,
        options.getCurrentMember,
        getChecklist,
        options.documentManualVerificationRequired$,
        documentManualVerificationVm,
        createDocumentPickerViewModel(job),
        new TxfJobCardViewModel(
          job,
          options.getCurrentMember,
          txfJobStore,
          options.jobDocuments,
          options.createAlertRetryWrapper,
        ),
        createJobDocumentListViewModel,
        options.searchDocuments,
        options.documentTypes$,
        options.archiveDocument,
        options.downloadDocuments,
        createUploadBarViewModel,
        createDocumentCreationModalViewModel,
      )

      const documentRequestDialogVm = new DocumentRequestDialogViewModel(
        job.id,
        options.fetchDocumentTypes,
        documentRequestStore,
        options.createAlertErrorHandler,
        getChecklist,
      )

      const jobProgressionVm = new JobProgressionViewModel(
        progressionStore,
        availableJobProgressionFlowsStore,
        () => vm.requestCloseAsCollaborator(),
        () => documentsVm.createDocumentCreationModalViewModel([]),
        () => documentsVm.documents,
        () => documentRequestDialogVm.promptForDocumentRequest(),
        job,
        options.createAlertErrorHandler,
        options.createAlertRetryWrapper,
        jobStore,
        jobScopeChangeStore,
        options.primeMemberByPublicId,
      )

      const ratingsVm = new RatingsViewModel(
        job,
        () => collaboratorsVm.canTransferAsAdmin,
        ratingsStore,
        options.newAlert,
        options.createAlertErrorHandler,
        options.createAlertRetryWrapper,
        jobProgressionVm,
      )

      const collaboratorsVm: JobCollaboratorsViewModel =
        new JobCollaboratorsViewModel(
          job,
          () => vm.currentTeamMemberOfOwnerTeam,
          jobStore,
          options.getCurrentMember,
          options.fetchTeamMember,
          options.createAlertErrorHandler,
          ratingsVm,
          new AddCollaboratorsAsAdminDialogViewModel(
            job,
            () => collaboratorsVm.canAddClientsAsAdmin,
            () => collaboratorsVm.canAddChampionProviderAsAdmin,
            () => collaboratorsVm.canAddSupportingProviderAsAdmin,
            () => collaboratorsVm.canRequestChampionProviderAsAdmin,
            jobStore,
            options.searchMembers,
            options.listTeamMembers,
            providerStore,
            options.newAlert,
            options.getCurrentMember,
          ),
          new RemoveCollaboratorsAsAdminDialogViewModel(
            job,
            jobStore,
            options.newAlert,
            options.getCurrentMember,
          ),
          new AddClientsAsTeamMemberDialogViewModel(
            job,
            () => collaboratorsVm.canAddClientsAsTeamMember,
            jobStore,
            options.listTeamMembers,
            options.newAlert,
          ),
          new RemoveClientsAsNonAdminDialogViewModel(
            job,
            jobStore,
            options.newAlert,
          ),
          new RemoveClientTeamDialogViewModel(job, jobStore, options.newAlert),
          new TransferJobDialogViewModel(
            options.searchMembers,
            options.listTeamMembersForWorkspaceMember,
            jobStore,
            providerStore,
            options.newAlert,
            options.getCurrentMember,
            ratingsVm,
          ),
        )

      const billingVm: JobBillingViewModel = new JobBillingViewModel(
        job,
        collaboratorsVm,
        options.getCurrentMember,
        options.fetchCouponByCode,
        options.fetchBillingMethods,
        options.billingTransactions,
        options.fetchBillingTransactions,
        options.createAlertRetryWrapper,
        createApplyCouponAsAdminViewModel(job),
        new RemoveCouponAsAdminDialogViewModel(
          job,
          jobStore,
          () => billingVm.coupon,
          options.newAlert,
          options.createAlertErrorHandler,
        ),
        new ChargeOrRefundDialogViewModel(
          job,
          () => billingVm.canChargeJob,
          options.getTeam,
          jobStore,
          () => billingVm.availableBillingMethods,
          options.newAlert,
          options.createAlertErrorHandler,
        ),
        new AddManualInvoiceDialogViewModel(
          job,
          jobStore,
          options.newAlert,
          options.createAlertErrorHandler,
        ),
        new SplitPayoutDialogViewModel(
          job,
          options.searchMembers,
          jobStore,
          options.newAlert,
        ),
      )

      const requestScopeChangesVm = new RequestScopeChangesDialogViewModel({
        job,
        workspaceId: job.workspace.id,
        searchInventoryItemsForProvider:
          options.searchInventoryItemsForProvider,
        getCurrentMember: options.getCurrentMember,
        requestScopeChanges: ({ reason, items, inventoryItems }) =>
          jobScopeChangeStore.requestScopeChanges({
            jobId: job.id,
            reason,
            scopeChangeItems: items,
            scopeChangeInventoryItems: inventoryItems,
          }),
        createAlertRetryWrapper: options.createAlertRetryWrapper,
      })

      const scopeChangesVm: JobScopeChangesViewModel =
        new JobScopeChangesViewModel(
          job,
          options.getCurrentMember,
          options.getTeamMember,
          jobScopeChangeStore,
          requestScopeChangesVm,
          new ViewJobScopeChangeRequestDialogViewModel(),
          new ScopeChangeBannerViewModel(
            job,
            () => scopeChangesVm.scopeChangeRequests,
            (request) => scopeChangesVm.viewScopeChangeRequests(request),
          ),
          new UpdateJobScopeDialogViewModel(
            job,
            options.searchServiceItems,
            job.workspace.id,
            jobScopeChangeStore,
            options.createAlertRetryWrapper,
          ),
          new ServiceItemListDialogViewModel(job, options.getCurrentMember),
        )

      const detailsVm = new DetailsViewModel(
        job,
        options.getCurrentMember,
        jobStore,
        questionStore,
        createQuestionListViewModel,
        () => isFirmAdminInWorkspace(job.workspace.id),
      )

      const notesTabVm: NotesTabViewModel = new NotesTabViewModel(
        job,
        new NotesDialogViewModel(
          job.id,
          infoGatheringStore,
          () => notesTabVm.canEditNotes,
          options.createAlertErrorHandler,
        ),
        getChecklist,
        isFirmAdminInWorkspace,
      )

      const capturedInformationVm: JobCapturedInformationViewModel =
        new JobCapturedInformationViewModel(
          job,
          detailsVm,
          infoGatheringStore,
          options.getCurrentMember,
          new InfoGatheringCardViewModel(
            job,
            options.createAlertErrorHandler,
            getChecklist,
            jobWorkspaceMember,
            jobStore,
          ),
          notesTabVm,
          individualProfileStore,
          businessProfileStore,
          taxRefundMethodStore,
          new EditPersonalProfileDialogViewModel(
            job,
            () => capturedInformationVm.personalProfileVm,
            () => capturedInformationVm.spousalProfileVm,
            () => capturedInformationVm.dependentProfileVm,
            individualProfileStore,
            infoGatheringStore,
            options.createAlertErrorHandler,
          ),
          new EditBusinessProfileDialogViewModel(
            job,
            () => capturedInformationVm.businessInfoVm,
            businessProfileStore,
            infoGatheringStore,
            options.createAlertErrorHandler,
          ),
          new EditRefundDialogViewModel(
            job,
            () => capturedInformationVm.taxRefundMethodVm,
            taxRefundMethodStore,
            infoGatheringStore,
            options.createAlertErrorHandler,
          ),
          documentRequestDialogVm,
        )

      const consultationVm: JobConsultationViewModel =
        new JobConsultationViewModel(
          job,
          jobWorkspaceMember,
          jobStore,
          consultationStore,
          options.navigate,
          options.createAlertErrorHandler,
          options.createAlertRetryWrapper,
          options.getAddToCalendarDelegate,
          createRoomViewModel(() => job.consultation?.room.id),
          new ConsultationPendingBookingModalViewModel(),
          new IncompleteConsultationCompletionDialogViewModel(
            (reason, otherReason) =>
              consultationVm.requestCloseWithIncompleteConsultation(
                reason,
                otherReason,
              ),
          ),
        )

      const bookkeepingGrantAccessDialogVm =
        new BookkeepingGrantAccessDialogViewModel(
          job,
          () => vm.isClientCollaborator,
          bookkeepingProgressionStore,
          options.newAlert,
          options.createAlertErrorHandler,
        )
      const bookkeepingCorrectCycleDialogVm =
        new BookkeepingCorrectCycleDialogViewModel(
          options.newAlert,
          options.createAlertErrorHandler,
          job.id,
          bookkeepingProgressionStore,
          () => bookkeepingVm.showSelectCycleDialog(),
        )
      const bookkeepingVm: JobBookkeepingViewModel =
        new JobBookkeepingViewModel(
          job,
          options.getCurrentMember,
          bookkeepingProgressionStore,
          new BookkeepingProgressionViewModel(
            bookkeepingProgressionStore,
            jobStore,
            jobScopeChangeStore,
            requestScopeChangesVm,
            bookkeepingGrantAccessDialogVm,
            job,
            () => bookkeepingVm.canManageBookkeeping,
            () => vm.isClientCollaborator,
            options.newAlert,
            options.createAlertErrorHandler,
          ),
          bookkeepingGrantAccessDialogVm,
          bookkeepingCorrectCycleDialogVm,
          new BookkeepingSelectCycleDialogViewModel(
            job.id,
            bookkeepingProgressionStore,
            bookkeepingCorrectCycleDialogVm,
          ),
        )

      const actionsVm: JobActionsViewModel = new JobActionsViewModel(
        job,
        options.getCurrentMember,
        () => vm,
        jobStore,
        sureprepStore,
        options.newAlert,
        options.createAlertErrorHandler,
        options.renameShortcut$,
        jobCreationDialogViewModel,
        collaboratorsVm,
        billingVm,
        scopeChangesVm,
        bookkeepingVm,
        ratingsVm,
        documentsVm,
        jobProgressionVm.updateProgressionStepDialogVm,
        new RenameJobDialogViewModel(
          () => job,
          (newName) => jobStore.rename(job, newName),
          options.createAlertErrorHandler,
        ),
        new ForfeitDialogViewModel(
          job,
          jobStore,
          options.navigate,
          options.createAlertErrorHandler,
        ),
        new CorrectionsDialogViewModel(
          job,
          questionStore,
          jobStore,
          createQuestionListViewModel,
          options.newAlert,
          options.createAlertRetryWrapper,
        ),
        new CreateAmendmentDialogViewModel(
          job,
          () => actionsVm.canViewAmendments,
          jobStore,
          options.newAlert,
          options.createAlertErrorHandler,
        ),
        new CopyJobDialogViewModel(
          () => jobStore.copyJob(job),
          options.navigate,
          options.newAlert,
          options.createAlertErrorHandler,
        ),
        new CloseJobAsAdminDialogViewModel(
          job,
          jobStore,
          jobScopeChangeStore,
          options.fetchBillingProfile,
          options.billingMethods,
          () => options.getCurrentMember(job.workspace.id),
        ),
        new DeleteJobDialogViewModel(
          job,
          () => actionsVm.canDeleteJobAsCollaborator,
          () => actionsVm.canDeleteJobAsAdmin,
          jobStore,
          draftJobStore,
          options.navigate,
          options.newAlert,
          options.createAlertErrorHandler,
        ),
        new NotifyProsOfOpenJobDialogViewModel(
          job,
          () => actionsVm.canNotifyPros,
          jobStore,
          options.newAlert,
          options.createAlertErrorHandler,
        ),
        new SubmitJobDialogViewModel(
          job,
          draftJobStore,
          options.newAlert,
          options.createAlertErrorHandler,
          () => root.canUpdateJobAsAdmin,
        ),
        new UpdateDeadlineDialogViewModel(
          job,
          jobStore,
          options.newAlert,
          options.createAlertErrorHandler,
        ),
        new ReopenJobAsAdminDialogViewModel(job, jobStore, options.newAlert),
      )

      const conversationVm = createConversationViewModel(
        () => job.conversation ?? Conversation.none,
        job.currentUserCollaborator?.team ?? Team.none,
        {
          get canManageConversation() {
            return jobWorkspaceMember().permissions.adminConversationManage
          },
          get canUpdateJobProgressionAsClient() {
            return (
              job.championClient?.member.isCurrentUser ||
              Boolean(
                job.ownerTeam &&
                options.getTeamMember(
                  job.ownerTeam.id,
                  jobWorkspaceMember().user.publicId,
                ),
              )
            )
          },
          viewApproveDraftInReviewDialog: () =>
            jobProgressionVm.approveDraftInReviewDialogVm.modal.show(),
          viewRejectDraftInReviewDialog: () =>
            jobProgressionVm.rejectDraftInReviewDialogVm.modal.show(),
        },
      )

      const vm: JobDetailsViewModel = new JobDetailsViewModel(
        job,
        options.navigate,
        jobStore,
        jobScopeChangeStore,
        options.getCurrentMember,
        options.getTeamMember,
        options.getUserData,
        options.ensureTermsAccepted,
        (job) => root.requestClose$.next(job),
        options.roomEvents$,
        new JobConversationViewModel(
          job.id,
          conversationVm,
          createMessageBankDialogViewModel(
            () => Id.assert(job.workspace.id),
            (text) => conversationVm.setComposerText(text),
          ),
          options.desktopNotificationState$,
          options.setDesktopNotificationsEnabled,
          options.createAlertErrorHandler,
          options.createAlertRetryWrapper,
          progressionStore,
          options.newMessages$,
        ),
        documentsVm,
        detailsVm,
        new JobNotesViewModel(
          job,
          jobNoteStore,
          options.createAlertErrorHandler,
          jobWorkspaceMember,
          collaboratorsVm,
          options.getRelationManager,
          async () => {
            await options.fetchRelationManager(job)
          },
        ),
        new JobTicketsViewModel(
          job,
          jobTicketStore,
          options.createAlertRetryWrapper,
        ),
        new JobInsightsCardViewModel(
          job,
          jobStore,
          jobInsightStore,
          options.conversation,
          options.fetchHumanMessagesTotalCount,
          options.createAlertErrorHandler,
        ),
        new FinalReturnAmountsCardViewModel(
          job,
          jobStore,
          options.createAlertErrorHandler,
          jobWorkspaceMember,
        ),
        jobProgressionVm,
        collaboratorsVm,
        billingVm,
        scopeChangesVm,
        capturedInformationVm,
        consultationVm,
        bookkeepingVm,
        actionsVm,
      )

      runInAction(() => jobDetailsVms.add(vm))
      return vm
    },
    {
      keySelector: ([job]) => job.id,
      promise: ([, sig]) => sig,
    },
  )

  const createJobSearchViewModel = createJobSearchViewModelFactory(
    searchableJobStore,
    jobTicketStore,
    options.createAlertRetryWrapper,
    options.getCurrentMember,
    options.getUserData,
    options.getCurrentUserWorkspaceMemberships,
    options.filterTeamMembers,
    (query, signal) => publishedLegendStore.listLegends(query, signal),
    options.listUserTeams,
    options.searchServiceItems,
    options.fetchDocumentTypes,
    options.unreadJobIds$,
  )
  const createFilterStorage = (ns: string) =>
    new FilterStorage(localStorage, ns)
  const savedFilterStore = new SavedJobFilterStore(localStorage)

  const jobRootViewModel: JobRootViewModel = new JobRootViewModel(
    options.navigate,
    options.createAlertRetryWrapper,
    jobStore,
    options.getCurrentMember,
    options.getCurrentUserWorkspaceMemberships,
    options.getUserData,
    options.fetchUserData,
    options.fetchDocumentTypes,
    isJobListAutoHidden$,
    new JobListViewModel(
      jobTicketStore,
      createJobSearchViewModel,
      options.listUserTeamMembers,
      () => jobRootViewModel.currentJob,
      options.getCurrentUserWorkspaceMemberships,
      options.probeCanViewOpportunities,
      createFilterStorage,
      savedFilterStore,
      () => jobRootViewModel.canEditDraftJobAsAdmin,
      searchableJobStore,
      options.getWorkspaceSelection,
      isRefreshDesign,
    ),
    jobDetailsVms,
  )

  const createJobManagementViewModel = ({
    storageKey,
    ...vmOptions
  }: JobManagementViewModelOptions & { storageKey: string }) => {
    const searcher = createJobSearchViewModel({
      filterStorage: createFilterStorage(storageKey),
      savedFilterStore,
    })
    const getSelectedWorkspaces = () => searcher.selectedWorkspaceIds
    const columnsVm = new JobsTableColumnsViewModel(
      localStorage,
      () => searcher.getAllFilteredJobs(),
      options.getCurrentMember,
      options.getCurrentUserWorkspaceMemberships,
      options.newAlert,
      options.createAlertErrorHandler,
      options.getUserData,
      getSelectedWorkspaces,
      storageKey,
    )

    return new JobManagementViewModel(
      searcher,
      columnsVm,
      new JobListPageViewModel(
        searcher,
        columnsVm,
        localStorage,
        options.newAlert,
        options.createAlertErrorHandler,
        () => {
          // Never the email, which would end up in the link and its history.
          const member = options.getCurrentMember()
          const name =
            member.displayName ||
            `${member.givenName} ${member.familyName}`.trim()
          return name || 'A teammate'
        },
        (query) => `${window.location.origin}${links.jobs()}?${query}`,
        (text) => navigator.clipboard.writeText(text),
      ),
      new JobManagementSelectionViewModel(
        () => searcher.jobs,
        options.newAlert,
        options.createAlertErrorHandler,
        options.createAlertRetryWrapper,
        jobStore,
        options.getCurrentUserWorkspaceMemberships,
        getSelectedWorkspaces,
        () => searcher.archivedJobsFilter.value,
        searcher.removeJobs,
        searchableJobStore,
        searcher.pushJob,
        searcher.fetchAllJobs,
        () => searcher.workspacesFilter,
        options.searchMembers,
        options.listTeamMembersForWorkspaceMember,
        providerStore,
        jobCreationDialogViewModel,
        options.getCurrentMember,
        options.getTeamMember,
      ),
      new UpcomingConsultationPreviewViewModel(
        consultationStore,
        () => options.getUserData(options.getCurrentMember().user.publicId),
        options.createAlertRetryWrapper,
      ),
      new NotifyProsOfUnclaimedJobsDialogViewModel(
        jobStore,
        options.newAlert,
        options.createAlertErrorHandler,
        getSelectedWorkspaces,
      ),
      options.getCurrentUserWorkspaceMemberships,
      options.listUserTeamMembers,
      options.currentMember$,
      options.getCurrentMember,
      jobTicketStore,
      localStorage,
      options.getUserData,
      options.handleOfferResponse,
      options.getWorkspaceSelection,
      options.getTeamMember,
      options.createAlertErrorHandler,
      vmOptions,
    )
  }

  const fetchCheckoutLegendVersion: FetchCheckoutLegendVersionFn = (
    legendVersionId,
    signal,
  ) => publishedLegendStore.fetchSupportedLegendVersion(legendVersionId, signal)

  const checkoutVm = new CheckoutViewModel(
    draftJobStore,
    jobStore,
    fetchCheckoutLegendVersion,
    createApplyCouponAsAdminViewModel,
    options.fetchCouponByCode,
    options.billingProfiles$,
    options.fetchBillingProfile,
    options.getStripePublishableKey,
    options.addCardBillingMethod,
    options.navigate,
    options.newAlert,
    options.createAlertErrorHandler,
    options.getCurrentMember,
    options.getTeamMember,
  )

  const createJobCheckoutViewModel: JobCheckoutViewModelFactory = (jobId) =>
    new JobCheckoutViewModel(
      jobId,
      draftJobStore,
      jobStore,
      fetchCheckoutLegendVersion,
      createApplyCouponAsAdminViewModel,
      options.fetchCouponByCode,
      options.navigate,
      options.newAlert,
      options.createAlertErrorHandler,
      options.getCurrentMember,
      options.getTeamMember,
    )

  const firmDashboardViewModel = new FirmDashboardViewModel(
    searchableJobStore,
    jobStatsStore,
    jobTicketStore,
    createJobSearchViewModel,
    createFilterStorage,
    options.newAlert,
    options.createAlertErrorHandler,
    options.createAlertRetryWrapper,
    options.getCurrentMember,
    options.getCurrentUser,
    options.getCurrentUserWorkspaceMemberships,
    options.getTeamMembersForWorkspaceMember,
    options.listUserTeamMembers,
    () => jobRootViewModel.canEditDraftJobAsAdmin,
    options.currentMember$,
    localStorage,
  )

  const ocrScopingJobsViewModel = new OCRScopingJobsViewModel(
    options.getCurrentUserWorkspaceMemberships,
    ocrScopeWorkStore,
    draftJobStore,
    publishedLegendStore,
    options.searchMembers,
    options.searchDocuments,
    options.downloadDocument,
    options.currentMember$,
    options.getCurrentMember,
    options.listTeamMembersForWorkspaceMember,
    options.listTeamMembers,
    options.newAlert,
    options.createAlertErrorHandler,
    options.createAlertRetryWrapper,
    options.navigate,
    createJobCreationRootViewModel(),
  )

  const jobManagementViewModel = createJobManagementViewModel({
    storageKey: 'JobManagement',
    managesListPage: isRefreshDesign,
    managesConsultationPreview: true,
    retainsSearch: true,
  })

  const createUserJobsTabViewModel: UserJobsTabViewModelFactory = ({
    userPublicId,
    workspaces,
  }) =>
    new UserJobsTabViewModel(
      userPublicId,
      workspaces,
      createJobManagementViewModel({
        storageKey: 'UserDetails',
        managesListPage: false,
        managesConsultationPreview: false,
        retainsSearch: false,
      }),
    )

  const createQuestionListViewModel: QuestionListViewModelFactory = () =>
    new QuestionListViewModel(
      new DefaultQuestionAnswerEditorViewModelFactory({
        getAsOfDate: () => new Date(),
        createDocumentRequestsViewModel: () => {
          throw new Error('Documents questions are only asked on jobs.')
        },
      }),
    )

  const systemNotifications$ = incomingJobMessageNotifications({
    activeConversation$: toStream(
      () => jobRootViewModel.activeConversation,
      true,
    ),
    newMessages$: options.newMessages$,
    pageActivity$: options.pageActivity$,
    userActive$: options.userActive$,
    fetchWorkspace: options.fetchWorkspace,
    onAction: ({ jobId }) => options.navigate(links.job(jobId)),
  })

  return {
    jobStore,
    jobStatsStore,
    searchableJobStore,
    draftJobStore,
    publishedLegendStore,
    consultationStore,
    progressionStore,
    jobCreationRootVm,
    jobCreationDialogViewModel,
    couponModalVm,
    jobRootViewModel,
    acquireJobDetailsViewModel,
    jobManagementViewModel,
    checkoutVm,
    firmDashboardViewModel,
    ocrScopingJobsViewModel,
    createJobCreationRootViewModel,
    createJobCheckoutViewModel,
    createUserJobsTabViewModel,
    createQuestionListViewModel,
    promptCreateJob,
    systemNotifications$,
    workRealtime$,
  }
}
